// The Plan tab: which days a week shows, what sits on each day and in what
// order, the tray's four groups, and the faint previews of routines and
// repeats in later weeks. The Calendar tab places tasks with the same rules.
// Pure functions of the task set and today's date.

import type { Completion, DateStr, Task, TimeStr } from '@/types'
import { addDays, isoWeekday, startOfWeek } from './dates'
import { occurrencesBetween } from './recurrence'
import { isNotDone, isOpen, placeDay, routineWeek, timeOnDay } from './status'

export type WeekKind = 'past' | 'current' | 'future'

export function weekKind(weekStart: DateStr, today: DateStr): WeekKind {
  const monday = startOfWeek(today)
  return weekStart < monday ? 'past' : weekStart > monday ? 'future' : 'current'
}

/** The days a week shows: from today in the current week, Monday to Sunday in any other. */
export function shownDays(weekStart: DateStr, today: DateStr): DateStr[] {
  const out: DateStr[] = []
  for (let i = 0; i < 7; i += 1) {
    const d = addDays(weekStart, i)
    if (weekKind(weekStart, today) === 'current' && d < today) continue
    out.push(d)
  }
  return out
}

/** One row on a day or in a tray group. */
export interface PlanItem {
  key: string
  task: Task
  /** A ticked one-off or occurrence: one dense struck-through line. */
  done: boolean
  completion: Completion | null
  /** The day it was ticked, for done rows. */
  doneOn: DateStr | null
  /** A preview in a later week: half strength, opens read-only, can't be dragged or ticked. */
  faint: boolean
  /** Next week's routine, live because this week's is ticked. */
  ahead: boolean
  /** The time it keeps on its day, which puts it in the Calendar's hour grid. */
  time: TimeStr | null
  /** On the Calendar, a task's due date when it sits on another day: a marker of the deadline, not dragged. */
  dueMark: boolean
}

function card(t: Task, day: DateStr | null, over: Partial<PlanItem> = {}): PlanItem {
  return { key: t.id, task: t, done: false, completion: null, doneOn: null, faint: false, ahead: false, time: day ? timeOnDay(t, day) : null, dueMark: false, ...over }
}

/**
 * Open cards in the manual order, a timed one included, so anything can be dragged above
 * anything; previews after, done rows last.
 */
export function compareItems(a: PlanItem, b: PlanItem): number {
  const rank = (i: PlanItem) => (i.done ? 2 : i.faint ? 1 : 0)
  if (rank(a) !== rank(b)) return rank(a) - rank(b)
  if (a.done && b.done) return (a.completion?.completedAt.toMillis() ?? a.task.completedAt?.toMillis() ?? 0) - (b.completion?.completedAt.toMillis() ?? b.task.completedAt?.toMillis() ?? 0)
  return a.task.order - b.task.order || made(b.task) - made(a.task)
}

/** A pending write has no server timestamp yet: newest. */
const made = (t: Task) => t.createdAt?.toMillis() ?? Number.MAX_SAFE_INTEGER

/** Ticked one-offs and occurrences whose tick fell on `when`. */
export function doneRows(tasks: Task[], when: (d: DateStr) => boolean): PlanItem[] {
  const out: PlanItem[] = []
  for (const t of tasks) {
    if (t.kind === 'once' && t.done && t.doneOn && when(t.doneOn)) {
      out.push(card(t, null, { done: true, doneOn: t.doneOn }))
    }
    for (const c of t.completions ?? []) {
      if (when(c.doneOn)) out.push(card(t, null, { key: `${t.id}:${c.date}`, done: true, completion: c, doneOn: c.doneOn }))
    }
  }
  return out.sort((a, b) => (a.doneOn! < b.doneOn! ? -1 : a.doneOn! > b.doneOn! ? 1 : compareItems(a, b)))
}

/**
 * Everything on one day, in order: open tasks placed there (a routine on this
 * week's day, or next week's once this week's is ticked), faint previews in
 * later weeks, then what was ticked that day. Days already past keep anything
 * left open where it was planned, which the Calendar shows; the Plan tab
 * never shows past days.
 */
/**
 * `dueMarks`, for the Calendar: a due date always shows on its date, so a task planned for another day
 * appears a second time there, as a marker. The Plan tab places a task once, by its Do it on day.
 */
export function itemsOnDay(tasks: Task[], day: DateStr, today: DateStr, hideDone = false, dueMarks = false): PlanItem[] {
  const monday = startOfWeek(today)
  const week = startOfWeek(day)
  const out: PlanItem[] = []
  for (const t of tasks) {
    if (!isOpen(t)) continue
    if (t.kind === 'routine') {
      const r = routineWeek(t, today)
      if (t.waiting && t.followUpDate) {
        if (t.followUpDate === day) out.push(card(t, day))
        continue
      }
      if (week === monday) {
        if (!r.doneThisWeek && r.plannedDate === day) out.push(card(t, day))
      } else if (week > monday && r.plannedDate) {
        if (r.doneThisWeek && week === r.weekOf) {
          if (r.plannedDate === day) out.push(card(t, day, { ahead: true }))
        } else if (isoWeekday(r.plannedDate) === isoWeekday(day)) {
          out.push(card(t, day, { key: `${t.id}@${day}`, faint: true }))
        }
      }
      continue
    }
    if (placeDay(t, today) === day) out.push(card(t, day))
    if (t.kind === 'repeat' && week > monday && occurrencesBetween(t, day, day).includes(day)) {
      out.push(card(t, day, { key: `${t.id}@${day}`, faint: true, time: null }))
    }
  }
  if (dueMarks) {
    for (const t of tasks) {
      if (isOpen(t) && t.kind !== 'routine' && t.dueDate === day && placeDay(t, today) !== day) out.push(card(t, day, { key: `${t.id}:due`, dueMark: true }))
    }
  }
  if (!hideDone) out.push(...doneRows(tasks, (d) => d === day))
  return out.sort(compareItems)
}

/** A day's count: real open cards only, never previews or done rows. */
export function openCount(items: PlanItem[]): number {
  return items.filter((i) => !i.done && !i.faint && !i.dueMark).length
}

export interface Tray {
  routines: PlanItem[]
  /** Done earlier this week, with the day each was ticked. */
  done: PlanItem[]
  notdone: PlanItem[]
  nodate: PlanItem[]
}

export const TRAY_ORDER: (keyof Tray)[] = ['routines', 'done', 'notdone', 'nodate']

/**
 * The tray for the week shown. In the current week: routines not placed this
 * week, what was ticked on the days already past, open tasks whose day has
 * passed, and one-offs with no date. In a later week: routines unplaced there
 * (faint until this week's is ticked) and the same No date list. Past weeks
 * have no tray.
 */
export function trayGroups(tasks: Task[], weekStart: DateStr, today: DateStr, hideDone = false): Tray {
  const kind = weekKind(weekStart, today)
  const empty: Tray = { routines: [], done: [], notdone: [], nodate: [] }
  if (kind === 'past') return empty
  const monday = startOfWeek(today)
  const open = tasks.filter(isOpen)
  // every group keeps the hand-made order: an overdue task or an older day is marked, never pinned
  const nodate = open
    .filter((t) => t.kind === 'once' && placeDay(t, today) === null)
    .map((t) => card(t, null))
    .sort(compareItems)
  const routines: PlanItem[] = []
  for (const t of open) {
    if (t.kind !== 'routine' || (t.waiting && t.followUpDate)) continue
    const r = routineWeek(t, today)
    if (r.plannedDate) continue
    if (kind === 'current') {
      if (!r.doneThisWeek) routines.push(card(t, null))
    } else if (r.doneThisWeek && weekStart === r.weekOf) {
      routines.push(card(t, null, { ahead: true }))
    } else {
      routines.push(card(t, null, { key: `${t.id}@${weekStart}`, faint: true }))
    }
  }
  routines.sort(compareItems)
  if (kind === 'future') return { ...empty, routines, nodate }
  const done = hideDone ? [] : doneRows(tasks, (d) => d >= monday && d < today)
  const notdone = open
    .filter((t) => isNotDone(t, today))
    .map((t) => card(t, null))
    .sort(compareItems)
  return { routines, done, notdone, nodate }
}

/** The order that puts a new or dropped task at the end of a day's open tasks. */
export function orderAtEnd(items: PlanItem[]): number {
  let max = 0
  let any = false
  for (const i of items) {
    if (i.done || i.faint || i.dueMark) continue
    if (!any || i.task.order > max) max = i.task.order
    any = true
  }
  return any ? max + 1 : 0
}

/** The order that puts a task at the top of a list, as a new undated task goes. */
export function orderAtTop(items: PlanItem[]): number {
  let min = 0
  for (const i of items) if (!i.done && !i.faint && i.task.order < min) min = i.task.order
  return min - 1
}
